import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SocialActions from '../components/SocialActions';
import { 
  Globe, 
  Smartphone, 
  Megaphone, 
  Cloud, 
  Wrench, 
  ShieldCheck, 
  ChevronRight, 
  ArrowLeft,
  CheckCircle,
  Zap,
  Layout,
  Code,
  FileText,
  Database,
  ShoppingCart,
  Puzzle,
  Apple,
  Layers,
  TrendingUp,
  Target,
  Share2,
  Headphones,
  Bug,
  ArrowUpCircle,
  Clock,
  Smile,
  Users,
  Server,
  Cpu
} from 'lucide-react';

const serviceData: Record<string, any> = {
  'web': {
    title: 'Web Development',
    icon: Globe,
    color: 'from-blue-500 to-indigo-600',
    desc: 'Empowering your brand with high-performance, scalable, and visually engaging web solutions. From custom web applications to robust e-commerce platforms, we build for the future.',
    longDesc: 'Our web development team combines artistic vision with technical precision. We don\'t just build websites; we create digital experiences that resonate with your audience and drive measurable business results. Using the latest frameworks and a security-first approach, we ensure your web presence is fast, reliable, and secure.',
    features: [
      { title: 'UI/UX Design', icon: Layout, desc: 'User-centric design that guides your visitors naturally towards conversion.' },
      { title: 'Custom Apps', icon: Code, desc: 'Bespoke web applications tailored to your specific business logic and requirements.' },
      { title: 'CMS Solutions', icon: FileText, desc: 'Manage your content with ease using our flexible and intuitive CMS platforms.' },
      { title: 'ERP Integration', icon: Database, desc: 'Connect your business processes with seamless ERP and database integrations.' },
      { title: 'E-commerce', icon: ShoppingCart, desc: 'Turn browsers into buyers with high-converting, secure online store solutions.' },
      { title: 'Plugins', icon: Puzzle, desc: 'Extend functionality with custom-built components and browser extensions.' }
    ]
  },
  'mobile': {
    title: 'Mobile Development',
    icon: Smartphone,
    color: 'from-pink-500 to-rose-600',
    desc: 'Creating best-in-class mobile applications that surpass competitors and take advantage of advanced device capabilities across iOS and Android.',
    longDesc: 'In an increasingly mobile-first world, your application needs to be flawless. We specialize in developing native and cross-platform mobile apps that offer seamless performance, intuitive navigation, and deep integration with device hardware. Whether it\'s a consumer-facing app or an enterprise tool, we deliver excellence.',
    features: [
      { title: 'iOS Apps', icon: Apple, desc: 'Native Swift and SwiftUI development for the ultimate Apple ecosystem experience.' },
      { title: 'Android Apps', icon: Smartphone, desc: 'Powerful Kotlin and Java applications optimized for the vast Android market.' },
      { title: 'Cross-Platform', icon: Layers, desc: 'Efficient React Native and Flutter solutions that run beautifully on both platforms.' },
      { title: 'App Store SEO', icon: TrendingUp, desc: 'Strategies to ensure your app gets discovered by the right audience in the stores.' }
    ]
  },
  'digital-marketing': {
    title: 'Digital Marketing',
    icon: Megaphone,
    color: 'from-emerald-500 to-teal-600',
    desc: 'Data-driven marketing strategies to boost your online visibility and convert clicks into qualified leads through strategic SEO, SEM, and social media.',
    longDesc: 'Digital marketing is more than just ads; it\'s about connecting with your audience at the right moment. Our comprehensive approach combines technical SEO, creative content strategy, and ROI-focused paid campaigns to build a sustainable digital pipeline for your business growth.',
    features: [
      { title: 'SEO Mastery', icon: TrendingUp, desc: 'Ranking your business on page one through strategic keyword intelligence and on-page optimization.' },
      { title: 'Social Media', icon: Share2, desc: 'Building a robust brand presence and engaging your audience where they spend their time.' },
      { title: 'PPC Management', icon: Target, desc: 'High-converting ad campaigns on Google and Meta designed to maximize your ROI.' },
      { title: 'Content Strategy', icon: FileText, desc: 'Establishing authority through educational and engaging content that nurtures leads.' }
    ]
  },
  'cloud': {
    title: 'Cloud Solutions',
    icon: Cloud,
    color: 'from-amber-500 to-orange-600',
    desc: 'Leveraging modern cloud infrastructure for compute power, storage, and flexibility without up-front expenses or long-term commitments.',
    longDesc: 'The cloud is the backbone of modern digital enterprises. We help you navigate the complexities of AWS, Google Cloud, and Azure to build resilient, auto-scaling infrastructures. Our goal is to reduce your operational overhead while increasing your system\'s reliability and global reach.',
    features: [
      { title: 'AWS Architect', icon: Server, desc: 'Designing and managing sophisticated Amazon Web Services infrastructures.' },
      { title: 'Google Cloud', icon: Cloud, desc: 'Harnessing Google\'s powerful data and infrastructure tools for your applications.' },
      { title: 'SaaS Delivery', icon: Cpu, desc: 'Transitioning your software to an internet-based service model for global scalability.' },
      { title: 'Cloud Migration', icon: ArrowUpCircle, desc: 'Seamlessly moving your legacy data and apps to the cloud with zero downtime.' }
    ]
  },
  'maintenance': {
    title: 'Maintenance & Support',
    icon: Wrench,
    color: 'from-slate-700 to-slate-900',
    desc: 'Ensuring your digital platforms remain stable, secure, and up to date with 24/7 proactive monitoring and expert assistance.',
    longDesc: 'Launch is just the beginning. Our maintenance and support services provide you with peace of mind, knowing that experts are monitoring your systems around the clock. We proactively identify issues, optimize performance, and ensure your software evolves with your business.',
    features: [
      { title: '24/7 Support', icon: Headphones, desc: 'Dedicated assistance to resolve technical hurdles whenever they arise.' },
      { title: 'Bug Rectification', icon: Bug, desc: 'Swift detection and resolution of software glitches to maintain a secure environment.' },
      { title: 'Optimizations', icon: Zap, desc: 'Continuous performance tuning to ensure your system runs at peak efficiency.' },
      { title: 'Uptime Focus', icon: Clock, desc: 'Proactive strategies designed to eliminate downtime for critical services.' }
    ]
  },
  'security': {
    title: 'Security Solutions',
    icon: ShieldCheck,
    color: 'from-violet-500 to-purple-600',
    desc: 'Safeguarding your digital assets with advanced threat detection, vulnerability audits, and enterprise-grade secure controls.',
    longDesc: 'In an era of increasing cyber threats, security cannot be an afterthought. We provide comprehensive security audits, penetration testing, and continuous monitoring to protect your data and your reputation. Our security-first mindset is integrated into every solution we deliver.',
    features: [
      { title: 'Threat Audit', icon: ShieldCheck, desc: 'Identifying and neutralizing vulnerabilities before they can be exploited.' },
      { title: 'Vulnerability Mgmt', icon: Bug, desc: 'Continuous scanning and patching to maintain a robust security posture.' },
      { title: 'Secure Access', icon: Database, desc: 'Implementing enterprise-grade identity and access management controls.' },
      { title: 'Data Encryption', icon: Server, desc: 'Protecting your most sensitive information with advanced encryption standards.' }
    ]
  },
  'ui-ux': {
    title: 'UI/UX Design',
    icon: Layout,
    color: 'from-pink-500 to-rose-600',
    desc: 'Crafting intuitive and visually stunning user experiences that engage and convert. We blend aesthetics with functionality.',
    longDesc: 'Great design is invisible. We focus on creating seamless journeys for your users, ensuring that every interaction is meaningful and every screen is optimized for conversion. Our design process is rooted in user research and behavioral psychology.',
    features: [
      { title: 'User Research', icon: Users, desc: 'Understanding your audience\'s needs, behaviors, and pain points.' },
      { title: 'Prototyping', icon: Code, desc: 'Creating interactive wireframes and mockups to validate ideas early.' },
      { title: 'Visual Design', icon: Smile, desc: 'Crafting beautiful interfaces that reflect your brand identity perfectly.' },
      { title: 'Usability Testing', icon: CheckCircle, desc: 'Iterating based on real user feedback to ensure the best possible experience.' }
    ]
  }
};

const ServiceIconAnimation = ({ serviceId, size = 200, className = "" }: { serviceId: string, size?: number, className?: string }): JSX.Element => {
  
  const animations: Record<string, JSX.Element> = {
    'web': (
      <motion.svg width={size} height={size} viewBox="0 0 200 200" fill="none" className={className}>
        <motion.rect 
          x="30" y="40" width="140" height="100" rx="10" 
          stroke="currentColor" strokeWidth="4"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.line x1="30" y1="70" x2="170" y2="70" stroke="currentColor" strokeWidth="4" />
        <motion.circle cx="50" cy="55" r="4" fill="currentColor" />
        <motion.circle cx="65" cy="55" r="4" fill="currentColor" />
        <motion.circle cx="80" cy="55" r="4" fill="currentColor" />
        <motion.path 
          d="M60 100 L90 100 M60 115 L120 115" 
          stroke="currentColor" strokeWidth="4" strokeLinecap="round"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <motion.circle 
          cx="140" cy="110" r="15" 
          stroke="currentColor" strokeWidth="4"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </motion.svg>
    ),
    'mobile': (
      <motion.svg width={size} height={size} viewBox="0 0 200 200" fill="none" className={className}>
        <motion.rect 
          x="60" y="30" width="80" height="140" rx="15" 
          stroke="currentColor" strokeWidth="4"
        />
        <motion.circle cx="100" cy="155" r="6" stroke="currentColor" strokeWidth="2" />
        <motion.rect 
          x="75" y="50" width="50" height="15" rx="2" 
          fill="currentColor" opacity="0.2"
          animate={{ x: [75, 85, 75] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.rect 
          x="75" y="75" width="50" height="40" rx="4" 
          stroke="currentColor" strokeWidth="2"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <motion.path 
          d="M85 130 H115" stroke="currentColor" strokeWidth="4" strokeLinecap="round"
          animate={{ width: [0, 30] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      </motion.svg>
    ),
    'digital-marketing': (
      <motion.svg width={size} height={size} viewBox="0 0 200 200" fill="none" className={className}>
        <motion.path 
          d="M50 80 L90 80 L120 50 L120 150 L90 120 L50 120 Z" 
          stroke="currentColor" strokeWidth="4" strokeLinejoin="round"
          animate={{ rotate: [-2, 2, -2] }}
          transition={{ duration: 0.5, repeat: Infinity }}
        />
        <motion.path 
          d="M140 80 C150 90 150 110 140 120" 
          stroke="currentColor" strokeWidth="4" strokeLinecap="round"
          animate={{ opacity: [0, 1, 0], x: [0, 10, 20] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
        <motion.path 
          d="M155 70 C170 85 170 115 155 130" 
          stroke="currentColor" strokeWidth="4" strokeLinecap="round"
          animate={{ opacity: [0, 1, 0], x: [0, 10, 20] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
        />
        <motion.circle 
          cx="100" cy="100" r="70" 
          stroke="currentColor" strokeWidth="2" strokeDasharray="5 10"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
      </motion.svg>
    ),
    'cloud': (
      <motion.svg width={size} height={size} viewBox="0 0 200 200" fill="none" className={className}>
        <motion.path 
          d="M50 130 C30 130 30 100 50 100 C50 70 90 70 100 80 C110 60 160 60 170 100 C190 100 190 130 170 130 Z" 
          stroke="currentColor" strokeWidth="4" strokeLinejoin="round"
          animate={{ y: [-5, 5, -5] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.path 
          d="M80 140 L80 170 M100 140 L100 170 M120 140 L120 170" 
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          animate={{ strokeDashoffset: [0, 20] }}
          strokeDasharray="4 4"
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <motion.path 
          d="M70 115 H130" 
          stroke="currentColor" strokeWidth="8" strokeLinecap="round" opacity="0.1"
        />
      </motion.svg>
    ),
    'maintenance': (
      <motion.svg width={size} height={size} viewBox="0 0 200 200" fill="none" className={className}>
        <motion.g animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }}>
          <circle cx="100" cy="100" r="40" stroke="currentColor" strokeWidth="12" strokeDasharray="20 11.4" />
          <circle cx="100" cy="100" r="25" stroke="currentColor" strokeWidth="4" />
        </motion.g>
        <motion.g animate={{ rotate: -360 }} transition={{ duration: 6, repeat: Infinity, ease: "linear" }}>
          <circle cx="150" cy="140" r="25" stroke="currentColor" strokeWidth="8" strokeDasharray="15 4.6" />
          <circle cx="150" cy="140" r="15" stroke="currentColor" strokeWidth="3" />
        </motion.g>
        <motion.path 
          d="M40 40 L60 60 M40 60 L60 40" 
          stroke="currentColor" strokeWidth="4" strokeLinecap="round"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 180] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      </motion.svg>
    ),
    'security': (
      <motion.svg width={size} height={size} viewBox="0 0 200 200" fill="none" className={className}>
        <motion.path 
          d="M100 30 L160 50 V100 C160 140 130 170 100 180 C70 170 40 140 40 100 V50 L100 30 Z" 
          stroke="currentColor" strokeWidth="4" strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <motion.rect 
          x="40" y="60" width="120" height="10" 
          fill="currentColor" opacity="0.2"
          animate={{ y: [60, 150, 60] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />
        <motion.circle cx="100" cy="100" r="20" stroke="currentColor" strokeWidth="4" />
        <motion.path d="M100 100 L115 115" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      </motion.svg>
    ),
    'ui-ux': (
      <motion.svg width={size} height={size} viewBox="0 0 200 200" fill="none" className={className}>
        <motion.rect 
          x="40" y="40" width="120" height="120" rx="20" 
          stroke="currentColor" strokeWidth="4"
        />
        <motion.rect 
          x="60" y="60" width="30" height="30" rx="5" 
          fill="currentColor" opacity="0.3"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <motion.rect 
          x="100" y="60" width="40" height="10" rx="5" 
          fill="currentColor" opacity="0.3"
        />
        <motion.rect 
          x="100" y="80" width="40" height="10" rx="5" 
          fill="currentColor" opacity="0.3"
        />
        <motion.path 
          d="M130 130 L160 160 M145 145 L155 135" 
          stroke="currentColor" strokeWidth="4" strokeLinecap="round"
          animate={{ x: [-10, 10, -10], y: [-10, 10, -10] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
      </motion.svg>
    )
  };

  return animations[serviceId] || animations['web'];
};

const ServiceDetail = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (serviceId && serviceData[serviceId]) {
      setData(serviceData[serviceId]);
      window.scrollTo(0, 0);
    } else {
      // If service not found, we could redirect or show error
      // navigate('/services');
    }
  }, [serviceId]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#001226]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] min-h-screen transition-colors duration-500">
      {/* Hero Section */}
      <section className={`bg-gradient-to-br ${data.color} py-24 md:py-32 relative overflow-hidden`}>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-10 transition-colors group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back to Services
          </Link>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-6xl font-semibold text-white mb-8 tracking-tight">{data.title}</h1>
            <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-10">
              {data.desc}
            </p>
            <div className="flex flex-wrap gap-4">
               <Link to="/contact" className="px-8 py-4 bg-white text-slate-900 rounded-2xl font-semibold hover:scale-105 transition-all shadow-xl">
                 Get Started
               </Link>
               <a href="#features" className="px-8 py-4 bg-white/10 text-white rounded-2xl font-semibold hover:bg-white/20 transition-all border border-white/20">
                 Explore Features
               </a>
            </div>
          </motion.div>
        </div>
        
        {/* Background Decorative Icon */}
        <div className="absolute top-1/2 right-[-5%] -translate-y-1/2 opacity-10">
           <ServiceIconAnimation serviceId={serviceId || ''} size={400} className="text-white" />
        </div>
      </section>

      {/* Deep Dive Section */}
      <section className="py-24" id="features">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32">
             <div className="space-y-8">
                <h2 className="text-3xl md:text-4xl font-semibold text-[#4F46E5] dark:text-white leading-tight">Comprehensive Solutions Tailored for Your Growth</h2>
                <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                  {data.longDesc}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                   {[1, 2, 3, 4].map(i => (
                     <div key={i} className="flex items-center gap-3">
                        <CheckCircle size={20} className="text-blue-600 shrink-0" />
                        <span className="text-sm font-semibold text-[#4F46E5] dark:text-white">Premium Quality Delivery</span>
                     </div>
                   ))}
                </div>
             </div>
             <div className="relative">
                <div className={`aspect-square rounded-[3rem] bg-gradient-to-br ${data.color} opacity-5 absolute -inset-4 rotate-3`} />
                <div className="bg-white dark:bg-[#001a35] aspect-square rounded-[3rem] shadow-2xl border border-slate-100 dark:border-white/10 p-12 flex items-center justify-center">
                   <ServiceIconAnimation serviceId={serviceId || ''} size={160} className="text-blue-600" />
                </div>
             </div>
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#4F46E5] dark:text-white mb-4">Core Competencies</h2>
            <p className="text-slate-500 dark:text-slate-400">Deep expertise in every aspect of {data.title}.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.features.map((feature: any, i: number) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white dark:bg-[#001a35] p-8 rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-white/10 group"
              >
                <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all">
                  <feature.icon size={28} />
                </div>
                <h3 className="text-xl font-semibold text-[#4F46E5] dark:text-white mb-4">{feature.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>

          <SocialActions 
            url={window.location.href} 
            title={data.title} 
          />
        </div>
      </section>

      {/* FAQ-like CTA Section */}
      <section className="py-24 bg-slate-50 dark:bg-[#001a35]/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
           <h2 className="text-3xl font-semibold text-[#4F46E5] dark:text-white mb-8">Ready to Elevate Your Business?</h2>
           <p className="text-slate-500 dark:text-slate-400 mb-12 text-lg">
             Our team is ready to discuss your specific needs and engineer a custom solution that drives real results.
           </p>
           <Link to="/quote" className="inline-flex items-center gap-3 px-10 py-5 bg-blue-600 text-white rounded-2xl font-semibold hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 group text-center">
             Book a Free Consultation
             <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
           </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
